import React, { useState, useEffect } from 'react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';

const TeacherWelcomeCard = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [greeting, setGreeting] = useState('');
  const teacherName = sessionStorage.getItem('first_name') + ' ' + sessionStorage.getItem('last_name');

  const quotes = [
    {
      text: "To teach is to learn twice.",
      author: "Joseph Joubert"
    },
    {
      text: "A great teacher makes hard things easy.",
      author: "Ralph Waldo Emerson"
    },
    {
      text: "It is the supreme art of the teacher to awaken joy in knowledge.",
      author: "Albert Einstein"
    },
    {
      text: "Better than a thousand days of study is one day with a great teacher.",
      author: "Japanese Proverb"
    },
    {
      text: "Education is the movement from darkness to light.",
      author: "Allan Bloom"
    },
    {
      text: "The art of teaching is the art of assisting discovery.",
      author: "Mark Van Doren"
    },
    {
      text: "Teaching is the greatest act of optimism.",
      author: "Colleen Wilcox"
    },
    {
      text: "Education is the kindling of a flame, not the filling of a vessel.",
      author: "Socrates"
    },
    {
      text: "What the teacher is, is more important than what he teaches.",
      author: "Karl A. Menninger"
    },
    {
      text: "A teacher affects eternity; he can never tell where his influence stops.",
      author: "Henry Adams"
    }
  ];

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) {
      setGreeting('Good Morning');
    } else if (hour < 17) {
      setGreeting('Good Afternoon');
    } else {
      setGreeting('Good Evening');
    }

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % quotes.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [quotes.length]);

  return (
    <>
      <style>{`
        .tchWelcCard {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          height: 210px;
          position: relative;
        }

        .tchLeftSec {
          padding: 1.5rem;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .tchGreetWrap {
          margin-bottom: 1rem;
        }

        .tchGreetTxt {
          font-size: 1.5rem;
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 0.25rem 0;
        }

        .tchName {
          font-size: 1.8rem;
          font-weight: 500;
          color: #ffc53a;
          margin: 0;
        }

        .tchQuoteBox {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .tchQuoteLabel {
          font-size: 0.75rem;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 0.75rem;
        }

        .tchQuoteDisplay {
          margin-bottom: 1rem;
        }

        .tchQuoteContent {
          animation: fadeIn 0.4s ease-in;
        }

        @keyframes fadeIn {
          from { 
            opacity: 0;
            transform: translateY(5px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }

        .tchQuoteText {
          font-size: 1rem;
          line-height: 1.5;
          color: #1f2937;
          margin: 0 0 0.5rem 0;
          font-style: italic;
        }

        .tchQuoteAuthor {
          font-size: 0.85rem;
          color: #6b7280;
          font-weight: 500;
        }

        .tchRightSec {
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
        }

        .tchRightSec::before {
          content: '';
          position: absolute;
          width: 160px;
          height: 160px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          top: -40px;
          right: -40px;
        }

        .tchRightSec::after {
          content: '';
          position: absolute;
          width: 120px;
          height: 120px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          bottom: -24px;
          left: -24px;
        }

        .tchLottieWrap {
          position: relative;
          z-index: 1;
          width: 100%;
          height: 100%;
        }

        @media (max-width: 768px) {
          .tchLeftSec {
            padding: 1.25rem;
          }

          .tchRightSec {
            min-height: 240px;
          }

          .tchGreetTxt {
            font-size: 1.3rem;
          }

          .tchName {
            font-size: 1rem;
          }

          .tchQuoteText {
            font-size: 0.95rem;
          }

          /* Mobile background lottie */
          .tchMobileLottieBg {
            position: absolute;
            inset: 0;
            opacity: 0.25;
            pointer-events: none;
            z-index: 0;
          }
        }
      `}</style>

      <div>
        <div className="tchWelcCard blueborder">
          <div className="row g-0 h-100">
            <div className="col-md-7 tchLeftSec">
              <div className="tchGreetWrap">
                <h1 className="tchGreetTxt">Hello, {greeting}!</h1>
                <p className="tchName">{teacherName}</p>
              </div>

              <div className="tchQuoteBox">
                
                <div className="tchQuoteDisplay">
                  <div className="tchQuoteContent" key={currentSlide}>
                    <p className="tchQuoteText">"{quotes[currentSlide].text}"</p>
                    <p className="tchQuoteAuthor">— {quotes[currentSlide].author}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-5 tchRightSec">
              <div className="tchLottieWrap d-none d-md-block">
                <DotLottieReact
                src="https://lottie.host/3031b9dc-38cc-4767-afab-511bff0960a1/rC3kOhuVTK.lottie"
                loop
                autoplay
                />
              </div>
            </div>
          </div>

          {/* Mobile background lottie */}
          <div className="tchMobileLottieBg d-md-none">
            <DotLottieReact
              src="https://lottie.host/3031b9dc-38cc-4767-afab-511bff0960a1/rC3kOhuVTK.lottie"
              loop
              autoplay
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default TeacherWelcomeCard;